import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import ProductCard from '../ProductCard';
import api from '../../services/api';

const FeaturedBestsellersCarousel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await api.getFeaturedProducts();
        setProducts(data || []);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const scrollTo = (direction) => {
    if (!carouselRef.current) return;
    
    const container = carouselRef.current;
    const cardWidth = container.querySelector('.product-card-wrapper')?.offsetWidth || 280;
    const gap = 24;
    const scrollAmount = cardWidth + gap;
    
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setCurrentIndex(prev => Math.max(0, prev - 1));
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setCurrentIndex(prev => Math.min(products.length - 1, prev + 1));
    }
  };

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-white">
        <div className="wrap">
          <div className="flex gap-6 overflow-hidden">
            {['s1', 's2', 's3', 's4'].map((skel) => (
              <div key={skel} className="flex-shrink-0 w-72 animate-pulse">
                <div className="bg-[#E2E8F0] rounded-2xl aspect-square mb-4"></div>
                <div className="h-4 bg-[#E2E8F0] rounded mb-2"></div>
                <div className="h-4 bg-[#E2E8F0] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="featured-bestsellers-section">
      <div className="wrap">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-5 w-5 text-[#F59E0B] fill-[#F59E0B]" />
              <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider">Most Popular</span>
            </div>
            <h2 className="text-3xl lg:text-[40px] font-bold text-[#0B1F3A]">
              Bestselling Systems
            </h2>
          </div>
          
          {/* Navigation Arrows */}
          <div className="flex items-center gap-2 mt-4 sm:mt-0">
            <button
              onClick={() => scrollTo('left')}
              className="w-10 h-10 rounded-full border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F8FAFC] hover:border-[#0F766E]/30 transition-colors disabled:opacity-50"
              disabled={currentIndex === 0}
              aria-label="Previous"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="h-5 w-5 text-[#475569]" />
            </button>
            <button
              onClick={() => scrollTo('right')}
              className="w-10 h-10 rounded-full border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F8FAFC] hover:border-[#0F766E]/30 transition-colors disabled:opacity-50"
              disabled={currentIndex >= products.length - 4}
              aria-label="Next"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="h-5 w-5 text-[#475569]" />
            </button>
          </div>
        </div>

        {/* Product Carousel */}
        <div 
          ref={carouselRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <div 
              key={product.id}
              className="product-card-wrapper flex-shrink-0 w-[280px] snap-start"
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>

        {/* Scroll Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {products.map((product, index) => (
            <button
              key={`dot-${product.id}`}
              onClick={() => {
                if (carouselRef.current) {
                  const cardWidth = 280 + 24;
                  carouselRef.current.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
                  setCurrentIndex(index);
                }
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'w-8 bg-[#0F766E]' 
                  : 'bg-[#E2E8F0] hover:bg-[#CBD5E1]'
              }`}
              aria-label={`Go to product ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedBestsellersCarousel;
